import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tr } from '../../../i18n/i18n';
import i18n from '../../../i18n/i18n';
import {
  IAdminTeacher,
  IAdminTeacherDetail,
  IGetAdminTeachersResponse,
  IGetAdminTeacherByIdResponse,
  IUpdateAdminTeacherResponse,
} from '../../../interface/classveew-admin/admin-management.interface';
import {
  fetchAdminTeachers,
  fetchAdminTeacherById,
  updateAdminTeacherProfile,
} from '../../action/classveew-admin/admin-teacher.action';

interface IAdminTeacherState {
  teachers: IAdminTeacher[];
  selectedTeacher: IAdminTeacherDetail | null;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: IAdminTeacherState = {
  teachers: [],
  selectedTeacher: null,
  page: 1,
  pageSize: 20,
  total: 0,
  totalPages: 0,
  isLoading: false,
  error: null,
};

// Slice
const adminTeacherSlice = createSlice({
  name: 'adminTeacher',
  initialState,
  reducers: {
    clearAdminTeachers: (state) => {
      state.teachers = [];
      state.page = 1;
      state.total = 0;
      state.totalPages = 0;
    },
    clearSelectedTeacher: (state) => {
      state.selectedTeacher = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all teachers
      .addCase(fetchAdminTeachers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAdminTeachers.fulfilled,
        (state, action: PayloadAction<IGetAdminTeachersResponse>) => {
          state.isLoading = false;
          state.teachers = action.payload.data?.teachers || [];
          state.page = action.payload.data?.page || 1;
          state.pageSize = action.payload.data?.pageSize || 20;
          state.total = action.payload.data?.total || 0;
          state.totalPages = action.payload.data?.totalPages || 0;
          state.error = null;
        },
      )
      .addCase(fetchAdminTeachers.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message || tr('g.failed_to_fetch_teachers', 'Failed to fetch teachers');
        state.teachers = [];
      })
      // Fetch teacher by ID
      .addCase(fetchAdminTeacherById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAdminTeacherById.fulfilled,
        (state, action: PayloadAction<IGetAdminTeacherByIdResponse>) => {
          state.isLoading = false;
          state.selectedTeacher = action.payload.data?.teacher || null;
          state.error = null;
        },
      )
      .addCase(fetchAdminTeacherById.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_teacher', 'Failed to fetch teacher');
        state.selectedTeacher = null;
      })
      // Update teacher
      .addCase(updateAdminTeacherProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateAdminTeacherProfile.fulfilled,
        (state, action: PayloadAction<IUpdateAdminTeacherResponse>) => {
          state.isLoading = false;
          state.selectedTeacher = action.payload.data?.teacher || null;
          // Update in list if present
          const index = state.teachers.findIndex((t) => t.id === action.payload.data?.teacher?.id);
          if (index !== -1 && action.payload.data?.teacher) {
            state.teachers[index] = {
              ...state.teachers[index],
              first_name: action.payload.data.teacher.first_name,
              last_name: action.payload.data.teacher.last_name,
              full_name: action.payload.data.teacher.full_name,
              email: action.payload.data.teacher.email,
              status: action.payload.data.teacher.status,
            };
          }
          state.error = null;
        },
      )
      .addCase(updateAdminTeacherProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message || tr('g.failed_to_update_teacher', 'Failed to update teacher');
      });
  },
});

export const { clearAdminTeachers, clearSelectedTeacher } = adminTeacherSlice.actions;
export default adminTeacherSlice.reducer;
